import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import { t } from 'i18next';
import { State } from 'shared/interface';
import { useQuery } from 'shared/hooks/useQuery';
import { ScaleType } from 'shared/interface/enums';
import { chartTimeRangeMapperForNumber } from 'shared/util/utility';
import localizationConstants from 'shared/util/translation/constants';
import { chartColors } from 'shared/constants/constants';

import { IChartSettingsType, ISpan } from '../interface/futureTable';

export const useAdvanceChartConfig = () => {
	const location = useLocation();
	const params = useQuery();
	const { currencyView, chartConfig } = useSelector((state: State) => ({ ...state.siteConfig }));
	const [chartSettings, setChartSettings] = useState<IChartSettingsType[]>([]);
	const [isChartSettingUpdate, setIsChartSettingUpdate] = useState(true);
	const [forecastSources, setForecastSources] = useState<string[]>([]);
	const [materialSymbol, setMaterialSymbol] = useState('');
	const [isFutureTable, setIsFutureTable] = useState(false);
	const { isRedirectFromGlobalSearch } = useSelector((state: State) => state.chartConfigReducer);

	const { basis_symbol, boerse, symbol, currency } = useMemo(() => {
		return {
			basis_symbol: `${params.get('basis_symbol') || chartSettings[0]?.basis_symbol}`,
			boerse: `${params.get('boerse') || chartSettings[0]?.boerse}`,
			symbol: `${params.get('symbol') || chartSettings[0]?.symbol}`,
			currency: `${params.get('currency')}`
		};
	}, [chartSettings, params]);

	useEffect(() => {
		if (isRedirectFromGlobalSearch) {
			setIsFutureTable(false);
			setMaterialSymbol('');
		}
	}, [isRedirectFromGlobalSearch]);

	useEffect(() => {
		if (symbol && boerse) {
			setIsChartSettingUpdate(true);
		}
	}, [basis_symbol, boerse, symbol, currency]);

	const [scaleType, setScaleType] = useState<string>(ScaleType.PRICE_AXIS);
	const [currencyType, setCurrencyType] = useState<string>(currencyView);
	const [chartType, setChartType] = useState<string>(
		scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'line' : chartConfig.chart_type
	);
	const [spanObj, setSpanObj] = useState<ISpan>({ span: '1y' });

	useEffect(() => {
		if (location.state?.chartSelection) {
			if (location.state?.chartSelection?.chartView) {
				setScaleType(location.state?.chartSelection?.chartView || scaleType);
			} else {
				setScaleType(scaleType);
			}
			if (location.state?.chartSelection?.type) {
				if (location.state?.chartSelection?.chartView) {
					setChartType(
						location.state?.chartSelection?.chartView === ScaleType.PERCENTAGE_CHANGE ||
							location.state?.chartSelection?.chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
							? 'line'
							: location.state?.chartSelection.type
					);
				} else {
					setChartType(
						scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
							? 'line'
							: location.state?.chartSelection.type
					);
				}
			}
			if (location.state?.chartSelection?.customChartSpan?.von && location.state?.chartSelection?.customChartSpan?.bis) {
				setSpanObj({
					span: '',
					startDate: location.state?.chartSelection?.customChartSpan?.von,
					endDate: location.state?.chartSelection?.customChartSpan?.bis
				});
			} else if (location.state?.chartSelection?.span) {
				setSpanObj({ span: chartTimeRangeMapperForNumber([], location.state?.chartSelection.span) });
			}
			if (location.state?.chartSelection?.currencyType) {
				setCurrencyType(location.state?.chartSelection?.currencyType);
			} else {
				setCurrencyType(currencyView);
			}
		}
	}, [location.state?.chartSelection]);

	useEffect(() => {
		if (location.state?.advanceChartConfig && location.state?.advanceChartConfig?.chartSettings) {
			setChartSettings((prevChartSettings) => {
				const singleIndicators = prevChartSettings[0]?.indicators;
				if (prevChartSettings.length === location.state?.advanceChartConfig.chartSettings.length) {
					return location.state?.advanceChartConfig.chartSettings.map((state: any, index: number) => {
						return {
							...state,
							forecastedColor: prevChartSettings[index]?.forecastedColor || [],
							symbol: isFutureTable ? materialSymbol || '' : state.symbol || '',
							indicators: prevChartSettings.length > 1 ? state.indicators : singleIndicators,
							name: prevChartSettings[index]?.name || '',
							fields: prevChartSettings[index]?.fields || []
						};
					});
				}
				return location.state?.advanceChartConfig.chartSettings;
			});
		} else if (location.pathname.includes('excel/export')) {
			return;
		} else {
			setChartSettings((prevChartSettings) => {
				// If settings exist, update only necessary fields

				if (prevChartSettings.length) {
					return prevChartSettings.map((setting) => {
						return {
							...setting,
							symbol: setting.symbol,
							name: setting.name || '',
							boerse: setting.boerse,
							basis_symbol: setting.basis_symbol,
							currency: {
								label: currency !== 'null' ? currency : '',
								value: currency !== 'null' ? currency : ''
							},
							axis: {
								label:
									(setting.axis.label && setting.axis.label) ||
									((scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) && 'Left') ||
									'Right',
								value:
									(setting.axis.value && setting.axis.value) ||
									((scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) && 'left') ||
									'right'
							},
							color: chartConfig.chart_color,
							disallowed: 0,
							history: {
								label: t(localizationConstants.none),
								value: ''
							},
							id: uuid(),
							forecastedColor:
								!isEmpty(forecastSources) &&
								forecastSources.reduce((acc: any, key: any, idx: number) => {
									acc[key] = chartColors[idx] || chartColors[0];
									return acc;
								}, {})
						};
					});
				}

				// Initialize settings if empty
				return [
					{
						symbol,
						boerse,
						basis_symbol,
						currency: {
							label: currency !== 'null' ? currency : '',
							value: currency !== 'null' ? currency : ''
						},
						axis: {
							label: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'Left' : 'Right',
							value: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'left' : 'right'
						},
						color: chartConfig.chart_color,
						disallowed: 0,
						history: {
							label: t(localizationConstants.none),
							value: ''
						},
						id: uuid(),
						indicators: prevChartSettings.length ? prevChartSettings[0].indicators : [], // Preserve indicators
						name: prevChartSettings.length ? prevChartSettings[0].name : '',
						unit: {
							label: '',
							value: ''
						},
						forecastedColor: !isEmpty(forecastSources)
							? forecastSources.reduce((acc: any, key: any, idx: number) => {
									acc[key] = chartColors[idx] || chartColors[0];
									return acc;
							  }, {})
							: []
					}
				];
			});
		}
	}, [
		forecastSources,
		scaleType,
		currency,
		basis_symbol,
		location,
		symbol,
		boerse,
		isFutureTable,
		materialSymbol,
		location.pathname,
		chartConfig.chart_color
	]);

	return {
		chartSettings,
		setChartSettings,
		scaleType,
		setScaleType,
		chartType,
		setChartType,
		spanObj,
		setSpanObj,
		basis_symbol,
		boerse,
		symbol,
		currency,
		isChartSettingUpdate,
		setIsChartSettingUpdate,
		setForecastSources,
		forecastSources,
		setMaterialSymbol,
		setIsFutureTable,
		isFutureTable,
		currencyType,
		setCurrencyType
	};
};
